import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ContractCalendar from '../components/ContractCalendar';
import ContractTimeline from '../components/ContractTimeline';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';import {
  ScrollText, Upload, MapPin, Calendar, Clock, ChevronRight,
  AlertTriangle, FileText, Building2, Euro, X, Loader2, Check,
  ChevronDown, Filter, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

function computeContractStatus(entry) {
  if (entry.contract_status === 'opgezegd') return 'opgezegd';
  const end = entry.key_data?.dates?.end;
  if (!end) return 'actief';
  const now = new Date();
  const endDate = new Date(end);
  if (endDate < now) return 'verlopen';
  const diff = (endDate - now) / (1000 * 60 * 60 * 24);
  if (diff <= 365) return 'verloopt binnenkort';
  return 'actief';
}

const STATUS_STYLES = {
  actief: 'bg-green-50 text-green-700 border-green-200',
  'verloopt binnenkort': 'bg-amber-50 text-amber-700 border-amber-200',
  verlopen: 'bg-red-50 text-red-700 border-red-200',
  opgezegd: 'bg-slate-100 text-slate-500 border-slate-200',
};
const STATUS_LABELS_NL = {
  actief: 'Actief', 'verloopt binnenkort': 'Verloopt binnenkort',
  verlopen: 'Verlopen', opgezegd: 'Opgezegd',
};

const DOC_TYPE_LABELS = {
  huurovereenkomst: 'Huurovereenkomst', contract: 'Leverancierscontract',
};

function formatDateNL(dateStr) {
  if (!dateStr) return '-';
  try { return new Date(dateStr).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return '-'; }
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return Math.round((d - new Date()) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function urgencyClass(dateStr, thresholdDays = 180) {
  const days = daysUntil(dateStr);
  if (days === null) return '';
  if (days <= 90) return 'text-red-600 font-semibold';
  if (days <= thresholdDays) return 'text-amber-600 font-medium';
  return 'text-slate-600';
}

export default function Contracten() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') === 'kalender' ? 'kalender' : 'lijst';
  const setActiveTab = (val) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (val === 'kalender') next.set('tab', 'kalender');
      else next.delete('tab');
      return next;
    }, { replace: true });
  };
  const [contracts, setContracts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('location_id');
  const [sortDir, setSortDir] = useState('asc');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpload, setShowUpload] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const toggleExpand = (id) => setExpandedId(prev => (prev === id ? null : id));

  const fetchContracts = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', '50');
      // Filter for contract entries
      if (filterLocation) params.set('location_id', filterLocation);
      if (searchQuery) params.set('search', searchQuery);
      // We can't use category filter alone since contracts might be detected by key_data
      // So we fetch broadly and filter client-side for now
      const res = await api.get(`/entries?${params.toString()}`);
      // Filter to only contracts
      const all = res.data.entries.filter(e =>
        e.key_data?.document_type === 'huurovereenkomst' ||
        e.key_data?.document_type === 'contract' ||
        e.category === 'contracten'
      );
      setContracts(all);
      setTotal(all.length);
      setPages(1);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authAxios, page, filterLocation, searchQuery]);

  useEffect(() => {
    const load = async () => {
      try {
        const api = authAxios();
        const [locRes, statsRes] = await Promise.all([
          api.get('/locations'),
          api.get('/contracts/overview'),
        ]);
        setLocations(locRes.data);
        setStats(statsRes.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, [authAxios]);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  const locMap = {};
  locations.forEach(l => { locMap[l.location_id] = l.name; });

  // Client-side filtering + sorting
  let filtered = [...contracts];
  if (filterStatus) {
    filtered = filtered.filter(e => computeContractStatus(e) === filterStatus);
  }
  if (filterType) {
    filtered = filtered.filter(e => (e.key_data?.document_type || 'overig') === filterType);
  }

  filtered.sort((a, b) => {
    let va, vb;
    if (sortField === 'location_id') { va = locMap[a.location_id] || a.location_id; vb = locMap[b.location_id] || b.location_id; }
    else if (sortField === 'title') { va = a.title || ''; vb = b.title || ''; }
    else if (sortField === 'opzeg_voor') { va = a.key_data?.dates?.opzeg_voor || 'z'; vb = b.key_data?.dates?.opzeg_voor || 'z'; }
    else if (sortField === 'end') { va = a.key_data?.dates?.end || 'z'; vb = b.key_data?.dates?.end || 'z'; }
    else if (sortField === 'indexatie') { va = a.key_data?.dates?.indexatie || 'z'; vb = b.key_data?.dates?.indexatie || 'z'; }
    else { va = a[sortField] || ''; vb = b[sortField] || ''; }
    const cmp = String(va).localeCompare(String(vb));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortHeader = ({ field, children }) => (
    <th
      onClick={() => toggleSort(field)}
      className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium cursor-pointer hover:text-slate-600 transition-colors select-none whitespace-nowrap"
    >
      <span className="flex items-center gap-1">
        {children}
        {sortField === field && <span className="text-[#2E75B6]">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
      </span>
    </th>
  );

  return (
    <div className="space-y-4 max-w-[1600px]" data-testid="contracten-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-sm uppercase tracking-wide text-slate-600 dark:text-[#8B949E]">Contracten</h2>
          <p className="text-xs text-slate-400 mt-0.5">{stats?.total || 0} contracten</p>
        </div>
        <button
          data-testid="upload-contract-button"
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] active:scale-[0.98] transition-all shadow-sm"
        >
          <Upload className="w-4 h-4" />
          Upload Contract
        </button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="h-9" data-testid="contracten-tabs">
          <TabsTrigger value="lijst" className="text-xs px-4 gap-1.5" data-testid="contracten-tab-lijst">
            <ScrollText className="w-3.5 h-3.5" /> Lijst
          </TabsTrigger>
          <TabsTrigger value="kalender" className="text-xs px-4 gap-1.5" data-testid="contracten-tab-kalender">
            <Calendar className="w-3.5 h-3.5" /> Kalender
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {activeTab === 'kalender' ? (
        <div data-testid="contracten-kalender-tab">
          <ContractCalendar locations={locations} />
        </div>
      ) : (
      <>
      {/* Stats Strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="contracts-stats">
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4">
            <p className="text-xs text-slate-400 mb-1">Totaal contracten</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-[#E6EDF3] tabular-nums">{stats.total}</p>
          </div>
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4">
            <p className="text-xs text-slate-400 mb-1">Actief</p>
            <p className="text-2xl font-bold text-green-600 tabular-nums">{stats.active}</p>
          </div>
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4">
            <p className="text-xs text-slate-400 mb-1">Verloopt &lt;12 mnd</p>
            <p className="text-2xl font-bold text-amber-600 tabular-nums">{stats.expiring_12m}</p>
          </div>
          <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-4">
            <p className="text-xs text-slate-400 mb-1">Opzegmoment &lt;6 mnd</p>
            <p className="text-2xl font-bold text-red-600 tabular-nums">{stats.opzeg_6m}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-slate-400 hidden sm:block" />
          <div className="relative w-full sm:w-auto">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              data-testid="contract-search"
              type="text" value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Zoeken..."
              className="pl-8 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-xs w-full sm:w-40 focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30"
            />
          </div>
          <select data-testid="contract-filter-location" value={filterLocation} onChange={e => setFilterLocation(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-xs">
            <option value="">Alle locaties</option>
            {locations.filter(l => l.status === 'active').map(loc => (
              <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
            ))}
          </select>
          <select data-testid="contract-filter-status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-xs">
            <option value="">Alle statussen</option>
            <option value="actief">Actief</option>
            <option value="verloopt binnenkort">Verloopt binnenkort</option>
            <option value="verlopen">Verlopen</option>
            <option value="opgezegd">Opgezegd</option>
          </select>
          <select data-testid="contract-filter-type" value={filterType} onChange={e => setFilterType(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-xs">
            <option value="">Alle types</option>
            <option value="huurovereenkomst">Huurovereenkomst</option>
            <option value="contract">Leverancierscontract</option>
          </select>
          {(filterLocation || filterStatus || filterType || searchQuery) && (
            <button onClick={() => { setFilterLocation(''); setFilterStatus(''); setFilterType(''); setSearchQuery(''); }}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors">
              <X className="w-3 h-3" /> Reset
            </button>
          )}
        </div>
      </div>

      {/* Table (desktop) / Cards (mobile) */}
      {loading ? (
        <div className="animate-pulse space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-14 bg-white rounded-xl border border-slate-200/80" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] p-12 text-center">
          <ScrollText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500">Geen contracten gevonden.</p>
          <button onClick={() => setShowUpload(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2E75B6] text-white text-sm hover:bg-[#27649C] transition-colors">
            <Upload className="w-4 h-4" /> Upload Contract
          </button>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] overflow-hidden" data-testid="contracts-table">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-[#1C2128] border-b border-slate-200/80 dark:border-[#21262D]">
                <tr>
                  <th className="w-8 px-2 py-2.5" aria-label="Tijdlijn"></th>
                  <SortHeader field="location_id">Locatie</SortHeader>
                  <SortHeader field="title">Titel</SortHeader>
                  <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium">Type</th>
                  <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium">Partij</th>
                  <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium">Bedrag</th>
                  <SortHeader field="end">Looptijd</SortHeader>
                  <SortHeader field="opzeg_voor">Opzeggen voor</SortHeader>
                  <SortHeader field="indexatie">Indexatie</SortHeader>
                  <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider text-slate-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-[#21262D]">
                {filtered.map(entry => {
                  const status = computeContractStatus(entry);
                  const kd = entry.key_data || {};
                  const parties = kd.parties || {};
                  const fin = kd.financials || {};
                  const dates = kd.dates || {};
                  const partyName = parties.verhuurder || parties.leverancier || '-';
                  const amount = fin.monthly || fin.amount || '-';
                  const start = dates.start ? formatDateNL(dates.start) : '';
                  const end = dates.end ? formatDateNL(dates.end) : '';
                  const looptijd = start || end ? `${start || '?'} \u2192 ${end || '?'}` : '-';

                  return (
                    <React.Fragment key={entry.entry_id}>
                    <tr className="hover:bg-slate-50 dark:hover:bg-[#1C2128] transition-colors cursor-pointer group"
                      onClick={() => navigate(`/kennisbank/${entry.entry_id}`)} data-testid={`contract-row-${entry.entry_id}`}>
                      <td className="px-2 py-2.5">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(entry.entry_id); }}
                          className="p-1 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors"
                          data-testid={`contract-expand-${entry.entry_id}`}
                          aria-label={expandedId === entry.entry_id ? 'Tijdlijn verbergen' : 'Tijdlijn tonen'}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${expandedId === entry.entry_id ? 'rotate-180' : ''}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-[#8B949E]">
                          <MapPin className="w-3 h-3 text-slate-400" />
                          {locMap[entry.location_id] || entry.location_id}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-sm font-medium text-slate-800 dark:text-[#E6EDF3] group-hover:text-[#2E75B6] transition-colors line-clamp-1">
                          {entry.title}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400 border border-violet-200/50">
                          {DOC_TYPE_LABELS[kd.document_type] || 'Overig'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-[#8B949E] max-w-[150px] truncate">{partyName}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-700 dark:text-[#C9D1D9] font-medium tabular-nums">{amount}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-[#8B949E] whitespace-nowrap">{looptijd}</td>
                      <td className="px-3 py-2.5">
                        <span className={`text-xs whitespace-nowrap ${urgencyClass(dates.opzeg_voor)}`}>
                          {dates.opzeg_voor ? formatDateNL(dates.opzeg_voor) : '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-[#8B949E] whitespace-nowrap">
                        {dates.indexatie ? formatDateNL(dates.indexatie) : '-'}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[status] || STATUS_STYLES.actief}`}>
                          {STATUS_LABELS_NL[status] || status}
                        </span>
                      </td>
                    </tr>
                    {expandedId === entry.entry_id && (
                      <tr data-testid={`contract-timeline-row-${entry.entry_id}`}>
                        <td colSpan={10} className="p-0">
                          <ContractTimeline entry={entry} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-2" data-testid="contracts-cards">
            {filtered.map(entry => {
              const status = computeContractStatus(entry);
              const kd = entry.key_data || {};
              const parties = kd.parties || {};
              const fin = kd.financials || {};
              const dates = kd.dates || {};

              return (
                <div key={entry.entry_id}
                  data-testid={`contract-card-${entry.entry_id}`}
                  className="w-full bg-white dark:bg-[#161B22] rounded-xl border border-slate-200/80 dark:border-[#21262D] overflow-hidden">
                  <div onClick={() => navigate(`/kennisbank/${entry.entry_id}`)}
                    data-testid={`contract-card-nav-${entry.entry_id}`}
                    className="p-4 text-left cursor-pointer">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="text-sm font-medium text-slate-800 dark:text-[#E6EDF3] line-clamp-2">{entry.title}</h4>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[status]}`}>
                          {STATUS_LABELS_NL[status]}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleExpand(entry.entry_id); }}
                          className="p-1 rounded-md text-slate-400 hover:text-[#2E75B6] hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors"
                          data-testid={`contract-expand-${entry.entry_id}`}
                          aria-label={expandedId === entry.entry_id ? 'Tijdlijn verbergen' : 'Tijdlijn tonen'}
                        >
                          <ChevronDown className={`w-4 h-4 transition-transform ${expandedId === entry.entry_id ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{locMap[entry.location_id] || entry.location_id}</span>
                      {(parties.verhuurder || parties.leverancier) && (
                        <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{parties.verhuurder || parties.leverancier}</span>
                      )}
                      {(fin.monthly || fin.amount) && (
                        <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{fin.monthly || fin.amount}</span>
                      )}
                      {dates.opzeg_voor && (
                        <span className={`flex items-center gap-1 ${urgencyClass(dates.opzeg_voor)}`}>
                          <AlertTriangle className="w-3 h-3" />Opzeg: {formatDateNL(dates.opzeg_voor)}
                        </span>
                      )}
                    </div>
                  </div>
                  {expandedId === entry.entry_id && <ContractTimeline entry={entry} />}
                </div>
              );
            })}
          </div>
        </>
      )}
      </>
      )}

      {/* Upload Contract Slide-over */}
      {showUpload && (
        <ContractUploadPanel
          authAxios={authAxios}
          locations={locations}
          onClose={() => setShowUpload(false)}
          onSuccess={() => { setShowUpload(false); fetchContracts(); }}
        />
      )}
    </div>
  );
}


// ---- Upload Contract Slide-over Panel ----

function ContractUploadPanel({ authAxios, locations, onClose, onSuccess }) {
  const [step, setStep] = useState('upload'); // upload, processing, review
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadId, setUploadId] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const fileInputRef = useRef(null);

  // Review form
  const [form, setForm] = useState({
    title: '', location_id: '', category: 'contracten',
    parties_verhuurder: '', parties_huurder: '',
    amount: '', monthly: '',
    start_date: '', end_date: '', opzeg_voor: '', indexatie: '',
    tags: '',
  });

  const handleFileSelect = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setStep('processing');
    setUploading(true);

    try {
      const api = authAxios();
      const fd = new FormData();
      fd.append('files', f);
      const uploadRes = await api.post('/documents/upload', fd);
      const upload = uploadRes.data.uploads?.[0];
      if (!upload || upload.error) {
        toast.error(upload?.error || 'Upload mislukt');
        setStep('upload');
        return;
      }
      setUploadId(upload.upload_id);
      setUploading(false);
      setExtracting(true);

      // Extract
      const extractRes = await api.post(`/documents/${upload.upload_id}/extract`);
      const result = extractRes.data.extraction_result;
      setExtraction(result);

      // Pre-fill form from extraction
      if (result) {
        const kd = result.key_data || {};
        setForm(prev => ({
          ...prev,
          title: result.suggested_title || f.name.replace(/\.[^.]+$/, ''),
          location_id: result.suggested_location || '',
          parties_verhuurder: kd.parties?.verhuurder || kd.parties?.leverancier || '',
          parties_huurder: kd.parties?.huurder || '',
          amount: kd.financials?.amount || kd.amount || '',
          monthly: kd.financials?.monthly || '',
          start_date: kd.dates?.start || kd.start_date || '',
          end_date: kd.dates?.end || kd.end_date || '',
          opzeg_voor: kd.dates?.opzeg_voor || kd.opzeg_voor || '',
          indexatie: kd.dates?.indexatie || '',
          tags: (result.suggested_tags || []).join(', '),
        }));
      }
      setStep('review');
    } catch (e) {
      toast.error('Verwerking mislukt: ' + (e.response?.data?.error || e.message));
      setStep('upload');
    } finally {
      setUploading(false);
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Titel is verplicht'); return; }
    setSaving(true);

    try {
      const api = authAxios();
      const key_data = {
        document_type: extraction?.document_type || 'contract',
        parties: {},
        financials: {},
        dates: {},
      };
      if (form.parties_verhuurder) key_data.parties.verhuurder = form.parties_verhuurder;
      if (form.parties_huurder) key_data.parties.huurder = form.parties_huurder;
      if (form.amount) key_data.financials.amount = form.amount;
      if (form.monthly) key_data.financials.monthly = form.monthly;
      if (form.start_date) key_data.dates.start = form.start_date;
      if (form.end_date) key_data.dates.end = form.end_date;
      if (form.opzeg_voor) key_data.dates.opzeg_voor = form.opzeg_voor;
      if (form.indexatie) key_data.dates.indexatie = form.indexatie;

      // Determine compliance items
      const createCompliance = !!form.opzeg_voor;

      await api.post(`/documents/${uploadId}/confirm`, {
        title: form.title.trim(),
        category: 'contracten',
        location_id: form.location_id || 'all',
        tags: form.tags,
        key_data,
        create_compliance: createCompliance,
        compliance_title: createCompliance ? `Opzegmoment: ${form.title.trim()}` : undefined,
        compliance_type: createCompliance ? 'contract' : undefined,
        compliance_due_date: createCompliance ? form.opzeg_voor : undefined,
        compliance_recurrence: 'one-time',
      });

      toast.success('Contract opgeslagen');
      onSuccess();
    } catch (e) {
      toast.error('Opslaan mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-[#30363D] bg-white dark:bg-[#0D1117] text-sm text-slate-900 dark:text-[#E6EDF3] focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/40";
  const labelClass = "text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1 block";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="contract-upload-panel">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white dark:bg-[#161B22] shadow-2xl overflow-y-auto animate-slide-in-right">
        <div className="sticky top-0 z-10 bg-white dark:bg-[#161B22] border-b border-slate-200 dark:border-[#21262D] px-6 py-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900 dark:text-[#E6EDF3]">Upload Contract</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#21262D] transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6">
          {step === 'upload' && (
            <div
              className="border-2 border-dashed border-slate-300 dark:border-[#30363D] rounded-xl p-12 text-center hover:border-[#2E75B6] transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="contract-dropzone"
            >
              <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600 dark:text-[#8B949E] mb-1">Sleep een contract hierheen</p>
              <p className="text-xs text-slate-400">of klik om te selecteren (PDF, DOCX)</p>
              <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc" className="hidden"
                onChange={handleFileSelect} data-testid="contract-file-input" />
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center py-12">
              <Loader2 className="w-10 h-10 text-[#2E75B6] mx-auto mb-4 animate-spin" />
              <p className="text-sm font-medium text-slate-700 dark:text-[#C9D1D9]">
                {uploading ? 'Contract uploaden...' : 'AI analyseert document...'}
              </p>
              <p className="text-xs text-slate-400 mt-1">Dit duurt 5-15 seconden</p>
            </div>
          )}

          {step === 'review' && (
            <div className="space-y-4" data-testid="contract-review-form">
              {extraction && (
                <div className="p-3 rounded-lg bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30">
                  <p className="text-xs text-green-700 dark:text-green-400 font-medium flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5" />
                    AI heeft {Object.keys(extraction.key_data || {}).length} velden gedetecteerd. Controleer en pas aan.
                  </p>
                </div>
              )}

              <div>
                <label className={labelClass}>Titel *</label>
                <input data-testid="contract-title" type="text" value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })} className={inputClass} />
              </div>

              <div>
                <label className={labelClass}>Locatie</label>
                <select data-testid="contract-location" value={form.location_id}
                  onChange={e => setForm({ ...form, location_id: e.target.value })} className={inputClass}>
                  <option value="">Selecteer locatie...</option>
                  {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Verhuurder / Leverancier</label>
                  <input data-testid="contract-party" type="text" value={form.parties_verhuurder}
                    onChange={e => setForm({ ...form, parties_verhuurder: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Huurder</label>
                  <input type="text" value={form.parties_huurder}
                    onChange={e => setForm({ ...form, parties_huurder: e.target.value })} className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Bedrag</label>
                  <input type="text" value={form.amount}
                    onChange={e => setForm({ ...form, amount: e.target.value })} className={inputClass} placeholder="bijv. EUR 48.000" />
                </div>
                <div>
                  <label className={labelClass}>Maandelijks</label>
                  <input type="text" value={form.monthly}
                    onChange={e => setForm({ ...form, monthly: e.target.value })} className={inputClass} placeholder="bijv. EUR 4.000" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Startdatum</label>
                  <input data-testid="contract-start-date" type="date" value={form.start_date}
                    onChange={e => setForm({ ...form, start_date: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Einddatum</label>
                  <input data-testid="contract-end-date" type="date" value={form.end_date}
                    onChange={e => setForm({ ...form, end_date: e.target.value })} className={inputClass} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Opzeggen voor</label>
                  <input data-testid="contract-opzeg" type="date" value={form.opzeg_voor}
                    onChange={e => setForm({ ...form, opzeg_voor: e.target.value })} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Indexatie datum</label>
                  <input type="date" value={form.indexatie}
                    onChange={e => setForm({ ...form, indexatie: e.target.value })} className={inputClass} />
                </div>
              </div>

              <div>
                <label className={labelClass}>Tags</label>
                <input type="text" value={form.tags}
                  onChange={e => setForm({ ...form, tags: e.target.value })} className={inputClass} placeholder="bijv. huur, rotterdam" />
              </div>

              <div className="flex gap-3 pt-4 border-t border-slate-200 dark:border-[#21262D]">
                <button onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-slate-200 dark:border-[#30363D] text-sm text-slate-600 dark:text-[#8B949E] hover:bg-slate-50 dark:hover:bg-[#21262D] transition-colors">
                  Annuleren
                </button>
                <button data-testid="contract-save" onClick={handleSave} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#2E75B6] text-white text-sm font-medium hover:bg-[#27649C] disabled:opacity-50 transition-colors">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Opslaan
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
